<template>
  <div>
    <AppDialog
      :is-open="isDialogOpened"
      size="xxxl"
      hideFooter
      @cancel="handleCloseDialog"
      class="checklistGlobalAttachedFilesDialogStyle"
      data-locator="checklist-all-attachments-dialog"
    >
      <template #title>
        {{$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.title')}}
      </template>
      <template #body>
        <v-row no-gutters>
          <SearchTextField
            v-model="searchQuery"
            :disabled="headerActionsDisabled"
            :tooltipText="$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.searchBarPlaceholder')"
          />
          <v-spacer />
          <app-button
            :disabled="headerActionsDisabled"
            data-locator="download-all-button"
            is-icon-color-custom
            @click="downloadAllAttachedFiles"
          >
            <app-material-icon
              name="download"
              color="icon-inverted"
            />
            {{ $t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.downloadAllBtnLabel') }}
          </app-button>
        </v-row>
        <v-row class="my-4" no-gutters>
          <div v-if="globalTaskFolderPending" class="w-100 text-center">
            <v-progress-circular
              indeterminate
              size="64"
            />
          </div>
          <div v-else-if="globalTaskFolder.length === 0" class="mx-auto text-center">
            <v-img src="@/assets/img/documentIllustration.svg" width="325" class="mx-auto my-4" />
            <h3 class="text--medium text--bold text-text-primary">{{ $t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.emptyTitle') }}</h3>
            <p class="text--small text--regular text-text-secondary">{{ $t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.emptyText') }}</p>
          </div>
          <!-- We do a v-show here instead of a v-if / v-else to have a consistent table state when we refresh its state through actions -->
          <div v-show="!globalTaskFolderPending && globalTaskFolder.length !== 0" class="w-100">
            <div v-if="selectedDocuments.length > 0" class="my-4">
              <DocumentsActionBar/>
            </div>
            <ChecklistGlobalAttachedFilesDialogTable
              :selectedItems="selectedDocuments"
              @setSelectedItems="setSelectedItems"
              :items="tableItems"
              :mnemo="mnemo"
            />
          </div>
        </v-row>
      </template>
    </AppDialog>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapActions, mapMutations, mapState } from 'vuex'

import AppDialog from '@/common/dialogs/AppDialog.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import ChecklistGlobalAttachedFilesDialogTable from '@/project/checklist/dialogs/ChecklistGlobalAttachedFilesDialogTable.vue'
import DocumentsActionBar from '@/project/documents/DocumentsActionBar.vue'
import { GET_GLOBAL_TASK_FOLDER } from '@/store/modules/checklist/action_types'
import { SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN } from '@/store/modules/checklist/mutation_types'
import { LOAD_FOLDERS_TREE, SELECT_DOCUMENTS } from '@/store/modules/documents/action_types'
import { OPEN_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'


export default {
  name: 'ChecklistGlobalAttachedFilesDialog',
  mixins: [dialogMixin],
  components: {
    AppDialog,
    SearchTextField,
    ChecklistGlobalAttachedFilesDialogTable,
    DocumentsActionBar,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      searchQuery: '',
    }
  },
  computed: {
    ...mapState('checklist', ['globalTaskFolder', 'globalTaskFolderPending']),
    ...mapState('documents', ['selectedDocuments']),
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
      }, 1000),
    },
    headerActionsDisabled () {
      return this.globalTaskFolderPending || this.globalTaskFolder.length === 0
    },
    tableItems () {
      return this.search ? this.filteredGlobalTaskFolder : this.globalTaskFolder
    },
    filteredGlobalTaskFolder () {
      if (!this.search) return []
      return this.globalTaskFolder.filter((task) => {
        const searchQuery = this.search.toLowerCase()
        return task.basename.toLowerCase().includes(searchQuery) ||
          task.updateUser.toLowerCase().includes(searchQuery)
      })
    },
  },
  async created () {
    try {

      await this.LOAD_FOLDERS_TREE()
      await this.GET_GLOBAL_TASK_FOLDER(this.mnemo)
    } catch (e) {
      toast.error(this.$t('common.msgFailErrorOccurred'))
    }
  },
  // Done here because when going to another route it'd stay open otherwise
  // Since the parent doesn't set it back to false anymore in that case
  beforeUnmount () {
    this.SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN(false)
  },
  methods: {
    ...mapActions('checklist', [GET_GLOBAL_TASK_FOLDER]),
    ...mapMutations('checklist', [SET_GLOBAL_ATTACHED_FILES_DIALOG_IS_OPEN]),
    ...mapActions('documentsDownloadFiles', [OPEN_DOCUMENTS_DOWNLOAD_FILES]),
    ...mapActions('documents', [LOAD_FOLDERS_TREE, SELECT_DOCUMENTS]),
    downloadAllAttachedFiles () {
      this.OPEN_DOCUMENTS_DOWNLOAD_FILES(this.tableItems)
    },
    setSelectedItems(selectedItems) {
      this.SELECT_DOCUMENTS(selectedItems)
    },
    handleCloseDialog() {
      this.closeDialog()
      this.SELECT_DOCUMENTS([])
    },
  },
}
</script>

<style>
  .checklistGlobalAttachedFilesDialogStyle {
    z-index: 1006 !important;
  }
</style>
